import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { resetPassword } from '../services/authService';
import { icons } from '../components/ui/Icons';
import Button from '../components/ui/Button';
import toast from 'react-hot-toast';
import { forgotPasswordSchema } from '../utils/validationSchemas';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    const result = await resetPassword(data.email);
    setLoading(false);

    if (result.success) {
      toast.success(result.message);
      setSentEmail(data.email);
      setEmailSent(true);
    } else {
      toast.error(result.error);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-purple-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* Ícone de Sucesso */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              {icons.check({ className: "h-8 w-8 text-green-600" })}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Email Enviado!
            </h2>
            <p className="text-gray-600 mb-6">
              Enviamos um link de recuperação para <strong>{sentEmail}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Verifique sua caixa de entrada e spam. O link expira em 1 hora.
            </p>

            <Button
              onClick={() => navigate('/login')}
              variant="primary"
              className="w-full mb-3"
            >
              Ir para Login
            </Button>

            <button
              onClick={() => setEmailSent(false)}
              className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
            >
              Enviar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-purple-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo e Título */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              {icons.scissors({ className: "h-12 w-12 text-indigo-600" })}
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Esqueceu a Senha?</h2>
            <p className="mt-2 text-sm text-gray-600">
              Sem problemas! Digite seu email e enviaremos um link para redefinir sua senha.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                {...register('email')}
                className={`appearance-none block w-full px-3 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="seu@email.com"
                disabled={loading}
                autoFocus
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Botão Submit */}
            <Button
              type="submit"
              variant="primary"
              className="w-full py-3 text-base font-semibold"
              disabled={loading}
            >
              {loading ? 'Enviando...' : 'Enviar Link de Recuperação'}
            </Button>
          </form>

          {/* Link para Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm font-medium text-indigo-600 hover:text-indigo-500 flex items-center justify-center gap-2"
            >
              {icons.chevronLeft({ className: 'h-4 w-4' })}
              Voltar para login
            </Link>
          </div>
        </div>

        {/* Link para Registro */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Não tem uma conta?{' '}
            <Link
              to="/registro"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              Cadastre-se
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
